import React, { useCallback } from 'react';
import { Icon } from '../../../pages/DesignSystem';
import { useDayPicker, type CalendarDay } from '../../../hooks/useDayPicker';
import { Event } from '../../../types/event.types';
import './DayPicker.css';

const DAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

interface DayPickerProps {
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  onNextDay: () => void;
  onPreviousDay: () => void;
  events: Event[];
}

const DayPicker: React.FC<DayPickerProps> = ({
  selectedDate,
  onSelectDate,
  onNextDay,
  onPreviousDay,
  events,
}) => {
  const {
    isOpen,
    toggleOpen,
    close,
    goToNextMonth,
    goToPreviousMonth,
    calendarDays,
    closedLabel,
    openLabel,
  } = useDayPicker(selectedDate, events);

  const isToday = useCallback((date: Date) => {
    const now = new Date();
    return date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
  }, []);

  const handleDayClick = useCallback((day: CalendarDay) => {
    onSelectDate(day.date);
    close();
  }, [onSelectDate, close]);

  const handlePrevArrow = useCallback(() => {
    if (isOpen) {
      goToPreviousMonth();
    } else {
      onPreviousDay();
    }
  }, [isOpen, goToPreviousMonth, onPreviousDay]);

  const handleNextArrow = useCallback(() => {
    if (isOpen) {
      goToNextMonth();
    } else {
      onNextDay();
    }
  }, [isOpen, goToNextMonth, onNextDay]);

  return (
    <div className="day-picker">
      {/* Closed state: navigation bar */}
      <div className="day-picker__header">
        <button
          className="day-picker__arrow"
          onClick={handlePrevArrow}
          type="button"
          aria-label={isOpen ? 'Previous month' : 'Previous day'}
        >
          <Icon name="ChevronLeft" size={20} />
        </button>

        <button
          className="day-picker__label"
          onClick={toggleOpen}
          type="button"
        >
          <span className="day-picker__label-text">
            {isOpen ? openLabel : closedLabel}
          </span>
          {isToday(selectedDate) && !isOpen && (
            <span className="day-picker__today-dot" />
          )}
          <Icon
            name="ChevronDown"
            size={16}
            className={`day-picker__chevron ${isOpen ? 'day-picker__chevron--open' : ''}`}
          />
        </button>

        <button
          className="day-picker__arrow"
          onClick={handleNextArrow}
          type="button"
          aria-label={isOpen ? 'Next month' : 'Next day'}
        >
          <Icon name="ChevronRight" size={20} />
        </button>
      </div>

      {/* Open state: calendar grid */}
      {isOpen && (
        <div className="day-picker__calendar">
          {/* Day name headers */}
          <div className="day-picker__day-names">
            {DAY_HEADERS.map(name => (
              <span key={name} className="day-picker__day-name">{name}</span>
            ))}
          </div>

          {/* Day grid */}
          <div className="day-picker__days">
            {calendarDays.map((day, i) => (
              <button
                key={i}
                className={`day-picker__day
                  ${day.isSelected ? 'day-picker__day--selected' : ''}
                  ${day.isToday && !day.isSelected ? 'day-picker__day--today' : ''}
                  ${!day.isCurrentMonth ? 'day-picker__day--outside' : ''}
                `}
                onClick={() => handleDayClick(day)}
                type="button"
              >
                <span className="day-picker__day-number">{day.dayNumber}</span>
                {day.eventCount > 0 && (
                  <span className="day-picker__day-dot" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(DayPicker);
